import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Loader2, LogIn, Eye, EyeOff } from 'lucide-react';
import { authAPI, isAuthenticated } from '../../services/api';
import toast from '../../services/toast';
import { AuthSplitShell } from '../../components/auth/AuthSplitShell';
import Input from '../../components/ui/Input';
import { FEATURE_FLAG_DEFAULTS } from '../../lib/featureFlagDefaults';
import { authReturnToFromLocation, sanitizeAuthReturnTo } from '../../utils/authReturnTo';
import {
  authLabelClass,
  authPrimaryButton,
  authFooterClass,
  authFooterLink,
  authPasswordToggle,
  authOAuthButton,
  authDividerRow,
  authDividerLine,
  authDividerText,
} from '../../components/auth/authViewTheme';

const showPasswordLogin = FEATURE_FLAG_DEFAULTS['ui.auth.passwordLogin'] === true;
const showGoogleLogin = FEATURE_FLAG_DEFAULTS['ui.auth.googleLogin'] === true;

function GoogleIcon() {
  return (
    <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" aria-hidden>
      <title>Google</title>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = authReturnToFromLocation(location);
  const [showPassword, setShowPassword] = useState(false);
  const [googleConfigured, setGoogleConfigured] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({ defaultValues: { email: '', password: '' } });

  useEffect(() => {
    if (!showGoogleLogin) return;
    authAPI
      .getGoogleLoginStatus()
      .then((data) => setGoogleConfigured(data.configured === true))
      .catch(() => setGoogleConfigured(false));
  }, []);

  if (isAuthenticated()) {
    return <Navigate to="/" replace />;
  }

  if (!showPasswordLogin) {
    return <Navigate to="/" replace />;
  }

  const onSubmit = handleSubmit(async ({ email, password }) => {
    try {
      await authAPI.login(email.trim(), password);
      localStorage.setItem('isAuthenticated', 'true');
      const destination = sanitizeAuthReturnTo(returnTo) || '/';
      navigate(destination, { replace: true });
    } catch (err) {
      toast.error((err as Error).message || 'Sign in failed. Please try again.');
    }
  });

  const showGoogleOption = showGoogleLogin && googleConfigured;

  return (
    <AuthSplitShell title="Sign in" description="Sign in to VantagePoint with your email and password">
      <form className="space-y-5" onSubmit={onSubmit} noValidate>
        <div className="space-y-4">
          <div>
            <label htmlFor="login-email" className={authLabelClass}>
              Email
            </label>
            <Input
              id="login-email"
              type="email"
              autoComplete="email"
              placeholder="your@email.com"
              {...register('email', { required: true })}
            />
          </div>
          <div>
            <label htmlFor="login-password" className={authLabelClass}>
              Password
            </label>
            <div className="relative">
              <Input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Enter your password"
                className="pr-11"
                {...register('password', { required: true })}
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                className={authPasswordToggle}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" aria-hidden />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden />
                )}
              </button>
            </div>
          </div>
        </div>

        <button type="submit" disabled={isSubmitting} className={authPrimaryButton}>
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Signing in…
            </>
          ) : (
            <>
              <LogIn className="h-4 w-4 shrink-0" aria-hidden />
              Sign in
            </>
          )}
        </button>
      </form>

      <p className={`${authFooterClass} mt-3`}>
        <Link to="/forgot-password" className={authFooterLink}>
          Forgot password?
        </Link>
      </p>

      {showGoogleOption ? (
        <>
          <div className={authDividerRow}>
            <div className={authDividerLine} aria-hidden />
            <span className={authDividerText}>or</span>
            <div className={authDividerLine} aria-hidden />
          </div>
          <button
            type="button"
            onClick={() => authAPI.startGoogleLogin({ returnTo: returnTo ?? undefined })}
            className={authOAuthButton}
          >
            <GoogleIcon />
            Continue with Google
          </button>
        </>
      ) : null}

      <p className={authFooterClass}>
        <Link to="/" className={authFooterLink}>
          Back to home
        </Link>
      </p>
    </AuthSplitShell>
  );
}
